import { useTranslation } from "react-i18next";
import { useMemo } from "react";

import { SummaryCard } from "@/pages/Dashboard/components/SummaryCard";
import { Invoice } from "@/api/opb/opbTypes";
import { getDaysUntil, getDate, getDateFromYYYYMMDD } from "@/utils/date";
import { useDateFormatter } from "@/hooks/useDateFormatter";

interface NextPaymentDueProps {
  invoices: Invoice[];
  isLoading: boolean;
}

export function NextPaymentDue({ invoices, isLoading }: NextPaymentDueProps) {
  const { t } = useTranslation("dashboard");
  const { formatDateFromYYYYMMDD } = useDateFormatter();

  const nextUnpaidInvoice = useMemo(
    () =>
      invoices
        .filter((invoice) => invoice.balance > 0)
        .sort((a, b) => (getDate(a.dueDate) > getDate(b.dueDate) ? 1 : -1))[0],
    [invoices]
  );

  const daysUntilPayment = nextUnpaidInvoice
    ? getDaysUntil(getDateFromYYYYMMDD(nextUnpaidInvoice.dueDate))
    : null;

  const getDescription = () => {
    if (!nextUnpaidInvoice || daysUntilPayment == null) {
      return t("No invoices due");
    }
    if (daysUntilPayment < 0) {
      return (
        <span className="text-red-60">
          {t("Due days ago", {
            count: daysUntilPayment * -1,
          })}
        </span>
      );
    }
    return t("Due in days", {
      count: daysUntilPayment,
    });
  };

  return (
    <SummaryCard
      title={t("Next payment due date")}
      message={
        nextUnpaidInvoice
          ? formatDateFromYYYYMMDD(nextUnpaidInvoice.dueDate, "PP")
          : "—"
      }
      description={getDescription()}
      isLoading={isLoading}
      data-testid="next_payment_due-card"
    />
  );
}
